// Cascades a Question_number rename across the whole survey — skip/display
// logic, termination logic, row/column filterLogic, validation logic, and
// piping/ternary references — so renaming a question ID doesn't silently
// break every other question that depends on it.

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Whole-token match only: renaming "Q1" must not touch "Q10", "Q1A", "XQ1".
const buildRefRegex = (id) => new RegExp(`\\b${escapeRegExp(id)}\\b`, "g");

const replaceIdInString = (value, oldId, newId) => {
  if (typeof value !== "string" || !value) return value;
  const regex = buildRefRegex(oldId);
  if (!regex.test(value)) return value;
  return value.replace(buildRefRegex(oldId), newId);
};

const replaceIdInStringOrArray = (value, oldId, newId) => {
  if (Array.isArray(value)) return value.map((v) => replaceIdInString(v, oldId, newId));
  return replaceIdInString(value, oldId, newId);
};

// Rewrites [pipe: Q1...] and {{ Q1=1 ? ... }} references embedded inside
// rich-text fields (Qtitle / instructions). Only rewrites matches that sit
// inside a pipe/ternary construct, to avoid touching plain prose that
// happens to contain the same token.
const replacePipeAndTernaryRefs = (html, oldId, newId) => {
  if (typeof html !== "string" || !html) return html;
  const esc = escapeRegExp(oldId);

  let next = html.replace(
    new RegExp(`(\\[\\s*pipe\\s*:\\s*)${esc}(\\b)`, "gi"),
    `$1${newId}$2`
  );

  next = next.replace(buildRefRegex(oldId), (match, offset, str) => {
    const before = str.slice(0, offset);
    const openIdx = before.lastIndexOf("{{");
    const closeIdx = before.lastIndexOf("}}");
    const insideTernary = openIdx > closeIdx;
    return insideTernary ? newId : match;
  });

  return next;
};

export const renameQuestionReferences = (data, oldId, newId) => {
  if (!oldId || !newId || oldId === newId) return data;

  const updated = {};
  Object.entries(data).forEach(([key, question]) => {
    let q = { ...question };
    let touched = false;

    if (q.logics !== undefined) {
      const next = replaceIdInStringOrArray(q.logics, oldId, newId);
      if (next !== q.logics) { q.logics = next; touched = true; }
    }
    if (q.termLogic !== undefined) {
      const next = replaceIdInString(q.termLogic, oldId, newId);
      if (next !== q.termLogic) { q.termLogic = next; touched = true; }
    }
    if (q.piping !== undefined) {
      const next = replaceIdInString(q.piping, oldId, newId);
      if (next !== q.piping) { q.piping = next; touched = true; }
    }

    if (Array.isArray(q.rows)) {
      let rowsTouched = false;
      const newRows = q.rows.map((row) => {
        if (!row?.filterLogic) return row;
        const next = replaceIdInString(row.filterLogic, oldId, newId);
        if (next !== row.filterLogic) { rowsTouched = true; return { ...row, filterLogic: next }; }
        return row;
      });
      if (rowsTouched) { q.rows = newRows; touched = true; }
    }

    if (Array.isArray(q.columns)) {
      let colsTouched = false;
      const newCols = q.columns.map((col) => {
        if (!col?.filterLogic) return col;
        const next = replaceIdInString(col.filterLogic, oldId, newId);
        if (next !== col.filterLogic) { colsTouched = true; return { ...col, filterLogic: next }; }
        return col;
      });
      if (colsTouched) { q.columns = newCols; touched = true; }
    }

    if (q.oeparameters) {
      let oe = { ...q.oeparameters };
      let oeTouched = false;
      ["validationLogic", "term", "disabledCells"].forEach((field) => {
        if (oe[field] !== undefined) {
          const next = replaceIdInString(oe[field], oldId, newId);
          if (next !== oe[field]) { oe[field] = next; oeTouched = true; }
        }
      });
      if (oeTouched) { q.oeparameters = oe; touched = true; }
    }

    ["Qtitle", "instructions"].forEach((field) => {
      if (q[field] !== undefined) {
        const next = replacePipeAndTernaryRefs(q[field], oldId, newId);
        if (next !== q[field]) { q[field] = next; touched = true; }
      }
    });

    updated[key] = touched ? q : question;
  });

  // A hidden quota question is keyed off its source question's ID
  // (e.g. "Q1_HIDDEN_QUOTA"). Rename that alongside its source.
  const oldQuotaKey = `${oldId}_HIDDEN_QUOTA`;
  if (updated[oldQuotaKey]) {
    const quotaQ = {
      ...updated[oldQuotaKey],
      Question_number: `${newId}_HIDDEN_QUOTA`,
    };
    delete updated[oldQuotaKey];
    updated[`${newId}_HIDDEN_QUOTA`] = quotaQ;
  }

  return updated;
};